import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import type { MRT_SortingState } from "material-react-table";

interface UseSortUrlSyncOptions {
  /** Default sort field (if no sort in URL) */
  defaultSortField?: string;

  /** Default sort direction (default: 'desc') */
  defaultSortDirection?: "asc" | "desc";

  /** URL parameter name for sort (default: 'sort') */
  sortParamName?: string;

  /** Callback when sorting changes */
  onSortChange?: (sorting: MRT_SortingState) => void;

  /** Debounce delay for URL updates (ms) */
  debounceMs?: number;

  /** Base path for URL (defaults to current pathname) */
  basePath?: string;
}

interface UseSortUrlSyncReturn {
  /** Current sorting state in MRT format */
  sorting: MRT_SortingState;

  /** Set sorting state (MRT format) */
  setSorting: (
    sorting: MRT_SortingState | ((prev: MRT_SortingState) => MRT_SortingState),
  ) => void;

  /** Get sort parameter for API call (e.g., "-create_date" or "file_name") */
  getSortParam: () => string | null;

  /** Clear sorting */
  clearSort: () => void;

  /** Current sort field (convenience accessor) */
  sortField: string | undefined;

  /** Current sort direction (convenience accessor) */
  sortDirection: "asc" | "desc" | undefined;

  /** Whether sorting is active */
  hasSorting: boolean;

  /** Whether a sort update is pending */
  isPending: boolean;
}

/**
 * Parse sort parameter from URL
 * Format: "-field" for descending, "field" for ascending
 */
function parseSortParam(
  sortParam: string | null,
): { field: string; desc: boolean } | null {
  if (!sortParam) return null;

  if (sortParam.startsWith("-")) {
    return { field: sortParam.slice(1), desc: true };
  }
  return { field: sortParam, desc: false };
}

/**
 * Convert MRT sorting state to URL parameter
 */
function sortingToParam(sorting: MRT_SortingState): string | undefined {
  if (sorting.length === 0) return undefined;

  const { id, desc } = sorting[0];
  return desc ? `-${id}` : id;
}

/**
 * Convert URL parameter to MRT sorting state
 */
function paramToSorting(sortParam: string | null): MRT_SortingState {
  const parsed = parseSortParam(sortParam);
  if (!parsed) return [];

  return [{ id: parsed.field, desc: parsed.desc }];
}

/**
 * Hook to synchronize sorting state with URL parameters
 *
 * Features:
 * - Bidirectional sync between state and URL
 * - Uses industry-standard sort format: "-field" for desc, "field" for asc
 * - Browser history integration (back/forward works)
 * - Compatible with Material React Table's MRT_SortingState
 */
export function useSortUrlSync(
  options: UseSortUrlSyncOptions = {},
): UseSortUrlSyncReturn {
  const {
    defaultSortField,
    defaultSortDirection = "desc",
    sortParamName = "sort",
    onSortChange,
    debounceMs = 100,
    basePath,
  } = options;

  const history = useHistory();
  const location = useLocation();

  // Ref to track if we're updating from our own changes
  const isInternalUpdate = useRef(false);
  const debounceTimer = useRef<NodeJS.Timeout>();
  const lastUrlRef = useRef(location.search);
  const onSortChangeRef = useRef(onSortChange);

  // Keep callback ref up to date
  useEffect(() => {
    onSortChangeRef.current = onSortChange;
  }, [onSortChange]);

  // Parse initial sorting from URL or use default
  const getInitialSorting = useCallback((): MRT_SortingState => {
    const params = new URLSearchParams(location.search);
    const sortParam = params.get(sortParamName);

    if (sortParam) {
      return paramToSorting(sortParam);
    }

    // Use default if provided
    if (defaultSortField) {
      return [{ id: defaultSortField, desc: defaultSortDirection === "desc" }];
    }

    return [];
  }, [location.search, sortParamName, defaultSortField, defaultSortDirection]);

  // State - initialized from URL
  const [sorting, setSortingState] =
    useState<MRT_SortingState>(getInitialSorting);
  const [isPending, setIsPending] = useState(false);

  // Update URL with debouncing
  const scheduleUrlUpdate = useCallback(
    (newSorting: MRT_SortingState) => {
      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      setIsPending(true);

      debounceTimer.current = setTimeout(() => {
        // Read URL at fire time (not closure time) to avoid overwriting
        // params that other hooks wrote during the debounce window.
        const liveSearch = window.location.search;
        const currentParams = new URLSearchParams(liveSearch);
        const sortParam = sortingToParam(newSorting);

        // Update sort param
        if (sortParam) {
          currentParams.set(sortParamName, sortParam);
        } else {
          currentParams.delete(sortParamName);
        }

        const newSearch = currentParams.toString();
        const searchWithPrefix = newSearch ? `?${newSearch}` : "";

        // Only update if URL actually changed
        if (liveSearch !== searchWithPrefix) {
          isInternalUpdate.current = true;
          lastUrlRef.current = searchWithPrefix;
          const currentPath = basePath || location.pathname;
          history.replace(`${currentPath}${searchWithPrefix}`);
          // Reset flag after a tick
          requestAnimationFrame(() => {
            isInternalUpdate.current = false;
          });
        }

        setIsPending(false);
      }, debounceMs);
    },
    [location.pathname, basePath, history, debounceMs, sortParamName],
  );

  // Listen for URL changes (browser back/forward only)
  useEffect(() => {
    // Skip if this was our own update
    if (isInternalUpdate.current || location.search === lastUrlRef.current) {
      return;
    }

    lastUrlRef.current = location.search;
    const params = new URLSearchParams(location.search);
    const sortParam = params.get(sortParamName);

    // When no sort param in URL, fall back to default sort (not empty).
    // The default sort is set in-memory but not written to the URL, so URL
    // changes from OTHER hooks (e.g. page navigation adding ?page=5) would
    // incorrectly clear the sort if we used paramToSorting(null) → [].
    const newSorting = sortParam
      ? paramToSorting(sortParam)
      : defaultSortField
        ? [{ id: defaultSortField, desc: defaultSortDirection === "desc" }]
        : [];

    // Only update state if sort actually changed (avoids new array reference triggering cascading re-renders)
    setSortingState((prev) => {
      if (
        prev.length === newSorting.length &&
        prev.every(
          (s, i) => s.id === newSorting[i].id && s.desc === newSorting[i].desc,
        )
      ) {
        return prev;
      }
      onSortChangeRef.current?.(newSorting);
      return newSorting;
    });
  }, [location.search, sortParamName, defaultSortField, defaultSortDirection]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // Set sorting (accepts MRT updater pattern)
  const setSorting = useCallback(
    (
      updater:
        | MRT_SortingState
        | ((prev: MRT_SortingState) => MRT_SortingState),
    ) => {
      setSortingState((prev) => {
        const newSorting =
          typeof updater === "function" ? updater(prev) : updater;
        scheduleUrlUpdate(newSorting);
        onSortChangeRef.current?.(newSorting);
        return newSorting;
      });
    },
    [scheduleUrlUpdate],
  );

  // Clear sorting
  const clearSort = useCallback(() => {
    setSortingState([]);
    scheduleUrlUpdate([]);
    onSortChangeRef.current?.([]);
  }, [scheduleUrlUpdate]);

  // Get sort param for API call
  const getSortParam = useCallback((): string | null => {
    return sortingToParam(sorting) ?? null;
  }, [sorting]);

  // Convenience accessors
  const sortField = sorting.length > 0 ? sorting[0].id : undefined;
  const sortDirection =
    sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : undefined;
  const hasSorting = sorting.length > 0;

  return {
    sorting,
    setSorting,
    getSortParam,
    clearSort,
    sortField,
    sortDirection,
    hasSorting,
    isPending,
  };
}
